import { IconButton } from '@mui/material';
import { DownloaderMediaType, useMyWalletDownloadFileLogic, useMyWalletLogic, walletPageMessages } from 'azshared';
import React, { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Card, CardSize } from '../../../../components/card/card';
import { AppDownloadFile } from '../../../../components/download-file/download-file';
import { AppSvgIcon } from '../../../../components/svg-icon/app-svg-icon';
import { AppTabBar } from '../../../../components/tab-bar/tab-bar';
import { useGlobalActions } from '../../../../hooks/global-actions';
import { downloadFile } from '../../../../utils/utils';
import { DossierFilter } from '../components/dossier-filter/dossier-filter';
import { FinancialPositionsSection } from '../components/financial-positions/financial-positions-section';
import { PerformanceSection } from '../components/performance/performance-section';
import { AppMyWalletSummary } from '../components/summary/my-wallet-summary';
import { Loader, LoaderSize } from '../../../../components/loader/loader';
import './my-wallet-section.scss';

export const MyWalletSection = (props: any) => {
  const { sectionMode } = props;

  const { formatMessage } = useIntl();
  const globalActions = useGlobalActions();
  const [downloadingType, setDownloadingType] = useState<DownloaderMediaType | null>(null);

  const {
    wallet,
    sortedMacroCategories,
    isWalletEmpty,
    isProductEmpty,
    summary,
    flagShowBalance,
    sections,
    selectedSection,
    isLoadingProductPerformances,
    isLoadingSummary,
    isLoadingWallet,
    onSelectedSection,
    setFlagShowBalance,
    closeProductDetails,
    goToRealEconomyDetailPage
  } = useMyWalletLogic(globalActions);

  const { onDownloadFile, fileDownloaded, fileDownloadedRequestStatus } = useMyWalletDownloadFileLogic(globalActions);

  const handleClickShowBalance = useCallback(
    (e: MouseEvent<HTMLButtonElement | HTMLLabelElement>) => {
      e.preventDefault();
      setFlagShowBalance(!flagShowBalance);
    },
    [flagShowBalance, setFlagShowBalance]
  );

  const handleKeyDownShowBalance = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement | HTMLLabelElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setFlagShowBalance(!flagShowBalance);
      }
    },
    [flagShowBalance, setFlagShowBalance]
  );

  const handleDownloadFile = useCallback(
    (mediaType: DownloaderMediaType) => {
      if (fileDownloadedRequestStatus === 'loading') {
        return;
      }
      setDownloadingType(mediaType);
      onDownloadFile(selectedSection === 'POSITION_LIST' ? 'holdings' : 'performance', mediaType);
    },
    [selectedSection, fileDownloadedRequestStatus, onDownloadFile]
  );

  useEffect(() => {
    if (fileDownloadedRequestStatus !== 'loading') {
      setDownloadingType(null);
    }
    if (fileDownloadedRequestStatus === 'succeeded' && (fileDownloaded.type === 'holdings' || fileDownloaded.type === 'performance')) {
      downloadFile(fileDownloaded.name, fileDownloaded.blob);
    }
  }, [fileDownloadedRequestStatus, fileDownloaded]);

  return (
    <div className='my-wallet-section'>
      <div className='my-wallet-section-icons'>
        <div className='my-wallet-section-icon my-wallet-section-icon-eye'>
          <IconButton onClick={handleClickShowBalance} onKeyDown={handleKeyDownShowBalance} tabIndex={0}>
            <AppSvgIcon icon={!flagShowBalance ? 'eye-blue' : 'blind-blue'} />
          </IconButton>
          <label onClick={handleClickShowBalance} onKeyDown={handleKeyDownShowBalance} tabIndex={0} className='my-wallet-section-icon-label'>
            {formatMessage(!flagShowBalance ? walletPageMessages.showBalance : walletPageMessages.blurBalance)}
          </label>
        </div>
        <DossierFilter />
      </div>
      <AppMyWalletSummary
        totalAmount={summary?.totalAmount}
        flagShowBalance={flagShowBalance}
        macroCategorySummary={summary?.macroCategories}
        isLoading={isLoadingSummary || isLoadingWallet}
      />

      <Card sizeVariant={CardSize.MAXI}>
        <AppTabBar tabs={sections} onTabSelected={onSelectedSection} value={selectedSection} />
        {isWalletEmpty && (
          <div className='empty-wallet'>
            <span>{formatMessage(walletPageMessages.emptyWallet)}</span>
          </div>
        )}
        {(selectedSection === 'PERFORMANCE' ? !isProductEmpty : !isWalletEmpty) && (
          <div className='my-wallet-section-icons my-wallet-section-icons-pdf'>
            {downloadingType === 'pdf' ? (
              <Loader loadersize={LoaderSize.XS} className='my-wallet-section-icon' />
            ) : (
              <AppDownloadFile mediaType='pdf' className='my-wallet-section-icon' onClick={handleDownloadFile}></AppDownloadFile>
            )}
            {downloadingType === 'xlsx' ? (
              <Loader loadersize={LoaderSize.XS} className='my-wallet-section-icon' />
            ) : (
              <AppDownloadFile mediaType='xlsx' className='my-wallet-section-icon' onClick={handleDownloadFile}></AppDownloadFile>
            )}
          </div>
        )}
        {selectedSection === 'POSITION_LIST' && !isWalletEmpty && (
          <FinancialPositionsSection
            macroCategories={sortedMacroCategories}
            closeProductDetails={closeProductDetails}
            isLoading={isLoadingWallet}
            flagShowBalance={flagShowBalance}
            goToRealEconomyDetailPage={goToRealEconomyDetailPage}
            sectionMode={sectionMode}
          />
        )}
        {selectedSection === 'PERFORMANCE' && !isWalletEmpty && (
          <PerformanceSection performanceList={wallet?.performances} isLoading={isLoadingProductPerformances} />
        )}
      </Card>
    </div>
  );
};
